import { valibotResolver } from "@hookform/resolvers/valibot";
import clsx from "clsx";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";

import useWorkspaceSubscriptionMutations from "features/subscriptions/datahooks/useWorkspaceSubscriptionMutations";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import TextArea from "common/components/ui/TextArea";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import {
  CancelSubscriptionFormSchema,
  type CancelSubscriptionFormSchemaType,
} from "common/schemas";

type CancelStep = "confirm" | "feedback";

interface CancelSubscriptionDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  shouldShowSurvey: boolean;
}

export default function CancelSubscriptionDialog({
  isOpen,
  setIsOpen,
  shouldShowSurvey,
}: CancelSubscriptionDialogProps) {
  const [step, setStep] = useState<CancelStep>("confirm");
  const isDesktop = useTwBreakpoint("md");

  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { workspace } = useWorkspaceDetails(workspaceId);
  const subscription = workspace?.subscription;

  const { cancelSubscription, isCancelingSubscription } =
    useWorkspaceSubscriptionMutations();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CancelSubscriptionFormSchemaType>({
    defaultValues: { reason: "" },
    resolver: valibotResolver(CancelSubscriptionFormSchema),
  });

  const resetDialogState = () => {
    setStep("confirm");
    reset({ reason: "" });
  };

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Close = isDesktop ? DialogClose : DrawerClose;

  async function onCancelSubscription(reason?: string) {
    await cancelSubscription({
      subscriptionId: subscription.id,
      reason,
    });
    setIsOpen(false);
  }

  const onSubmit = async ({ reason }: CancelSubscriptionFormSchemaType) => {
    await onCancelSubscription(reason);
  };

  let body: ReactNode;

  switch (step) {
    case "confirm":
      body = (
        <>
          <Title>Are you sure you want to cancel your subscription?</Title>

          <Description className="mb-6 md:mb-0">
            This action can’t be undone. All your campaigns activity will be
            cancelled and all seats will be unassigned
          </Description>

          <div className="mt-4 flex justify-between flex-row gap-4">
            <Close asChild>
              <Button variant="secondary-black">Back</Button>
            </Close>

            <Button
              variant="outline-danger"
              isLoading={isCancelingSubscription}
              onClick={
                shouldShowSurvey
                  ? () => setStep("feedback")
                  : () => onCancelSubscription()
              }
            >
              Cancel subscription
            </Button>
          </div>
        </>
      );
      break;
    case "feedback":
      body = (
        <form
          className="flex flex-col"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Title>One more thing before you leave</Title>

          <div className="flex mt-8 flex-col md:flex-row gap-4">
            <div className="overflow-hidden md:w-[267px] w-full rounded-4xl">
              <iframe
                src="https://player.vimeo.com/video/1176509674?badge=0&autopause=0&autoplay=1&player_id=0&app_id=58479&title=0&byline=0&portrait=0"
                title="Alex Cancellation"
                height={281}
                width={isDesktop ? 158 : 328}
                allow="autoplay; fullscreen"
                className="bg-black-950 mx-auto w-full"
              />
            </div>

            <div className="flex flex-col gap-4 justify-between">
              <div className="flex flex-col gap-4 text-black-950 [&>p]:mb-0">
                <p className="text-md-medium">
                  I'm sorry to see you go and would love to understand what led
                  you to cancel.
                </p>

                <p className="text-md-bold">
                  What can we do better in the future?
                </p>

                <div className="flex flex-col">
                  <span className="text-sm font-semibold">Alex</span>

                  <span className="text-black-800 text-sm-medium">
                    CEO Aimfox
                  </span>
                </div>
              </div>

              <FormField
                label=""
                htmlFor="cancel-subscription-reason"
                className="relative"
                error={errors.reason?.message}
              >
                <TextArea
                  id="cancel-subscription-reason"
                  autoFocus
                  minRows={3.5}
                  maxRows={4}
                  placeholder="Your feedback"
                  {...register("reason")}
                />

                <span
                  className={clsx([
                    "absolute right-2 text-xs",
                    watch("reason").length >= 2000
                      ? "text-red-500"
                      : "text-black-500",
                    errors.reason?.message ? "bottom-7" : "bottom-2",
                  ])}
                >
                  {watch("reason").length}/2000
                </span>
              </FormField>
            </div>
          </div>

          <div className="mt-4 flex justify-between flex-row gap-4">
            <Close asChild>
              <Button type="button" variant="secondary-black">
                Keep my plan
              </Button>
            </Close>

            <Button type="submit" isLoading={isCancelingSubscription}>
              Cancel my subscription
            </Button>
          </div>
        </form>
      );
      break;
  }

  return (
    <Component
      open={isOpen}
      onOpenChange={setIsOpen}
      onAfterClose={resetDialogState}
    >
      <Content size={step === "feedback" ? "lg" : "md"}>{body}</Content>
    </Component>
  );
}
